import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useSetupIntents, so applying both narrows rather than
// resets.
//
// ⚠️ THESE FILTERS ARE CLIENT-SIDE, over the pages loaded so far.
// `SetupIntentsFilterInput` declares `{customerId: String}` and NOTHING ELSE —
// verified against
// flexinet-stripe-api/src/appsync-api/src/schema/features/setupIntents.graphql.
// Unlike payment-methods (whose input declares `type`), this API cannot narrow
// by status or usage, so those two are matched in the browser.
//
// ⚠️ THEY MUST NOT BE SENT TO THE API. AppSync SILENTLY IGNORES an undeclared
// input field rather than erroring, so posting `{status: 'succeeded'}` into the
// filter would return the unfiltered list while the UI showed an active filter
// badge — the filter would look like it worked while doing nothing. That is why
// useSetupIntentsQuery builds its `serverFilter` from `customerId` alone rather
// than spreading `filters`.
//
// The honest consequence: a status filter narrows only what has been LOADED.
// The footer states a loaded count and never a dataset total, so the number
// next to a filtered list is never presented as "all matching rows".
export type SetupIntentsFilters = Record<string, string>;

let globalFilters: SetupIntentsFilters = {};
const filterListeners: Set<(filters: SetupIntentsFilters) => void> = new Set();

const notifyFilterChange = (filters: SetupIntentsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetSetupIntentsFilter = () => notifyFilterChange({});

export const useSetupIntentsFilter = () => {
    const [filters, setFilters] = useState<SetupIntentsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: SetupIntentsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
